// frontend/src/components/dashboard/OrderDetailsModal.js
import React from 'react';
import { X, ShoppingBag, Calendar, Package, DollarSign } from 'lucide-react';

const OrderDetailsModal = ({ order, onClose, onRepeat, repeatLoading = false }) => {
  if (!order) return null;

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'Ожидает обработки', color: 'bg-yellow-100 text-yellow-800' },
      'processing': { text: 'В обработке', color: 'bg-blue-100 text-blue-800' },
      'delivered': { text: 'Доставлен', color: 'bg-purple-100 text-purple-800' },
      'completed': { text: 'Завершен', color: 'bg-green-100 text-green-800' },
      'cancelled': { text: 'Отменен', color: 'bg-red-100 text-red-800' },
    };
    const statusInfo = statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6 text-[#4897d2] flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Заказ #{order.id}</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 flex items-center gap-1 sm:gap-2">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">
                  {new Date(order.created_at).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Status */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg">
            <span className="text-xs sm:text-sm font-medium text-gray-700">Статус заказа:</span>
            {getStatusBadge(order.status)}
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
              <Package className="w-4 h-4 sm:w-5 sm:h-5 text-[#4897d2] flex-shrink-0" />
              Состав заказа
            </h3>
            <div className="space-y-2 sm:space-y-3">
              {order.items && order.items.length > 0 ? (
                order.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm sm:text-base text-gray-900 mb-1 break-words">
                        {item.product?.name || item.product_name || 'Товар удален'}
                      </h4>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-600">
                        <span>Количество: {item.quantity}</span>
                        {item.product?.pack_quantity && (
                          <span className="text-xs text-gray-500">
                            ({item.product.pack_quantity} шт. в упаковке)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-left sm:text-right flex-shrink-0">
                      <div className="text-xs sm:text-sm text-gray-500">
                        {parseFloat(item.price || 0).toFixed(2)}₪ × {item.quantity}
                      </div>
                      <div className="font-semibold text-sm sm:text-base text-gray-900">
                        {parseFloat(item.total_price || 0).toFixed(2)}₪
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4 text-sm sm:text-base">Товары не найдены</p>
              )}
            </div>
          </div>

          {/* Order Notes */}
          {order.notes && (
            <div className="p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-2">Примечания к заказу:</h4>
              <p className="text-xs sm:text-sm text-gray-700 break-words">{order.notes}</p>
            </div>
          )}

          {/* Order Summary */}
          <div className="border-t border-gray-200 pt-4 sm:pt-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-[#4897d2] flex-shrink-0" />
                <span className="text-base sm:text-lg font-semibold text-gray-900">Итого:</span>
              </div>
              <span className="text-xl sm:text-2xl font-bold text-[#4897d2]">
                {parseFloat(order.total_price || 0).toFixed(2)}₪
              </span>
            </div>
            <div className="text-xs sm:text-sm text-gray-600">
              Товаров: {order.total_items || order.items?.length || 0}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 sm:p-6 space-y-3">
          {typeof onRepeat === 'function' && (
            <button
              onClick={onRepeat}
              disabled={repeatLoading}
              className="w-full px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold bg-white border border-[#4897d2] text-[#2f77b2] hover:bg-sky-50 transition-colors disabled:opacity-60"
            >
              {repeatLoading ? 'Добавляем в корзину...' : 'Повторить заказ'}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] text-white rounded-lg text-sm sm:text-base font-semibold hover:opacity-90 transition-opacity"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
